import React, { useRef } from 'react';
import styled from 'styled-components';
import { Cog } from '@styled-icons/fa-solid/Cog';
import { UndoAlt } from '@styled-icons/fa-solid/UndoAlt';
import { DoorOpen } from '@styled-icons/fa-solid/DoorOpen';
import { Shield } from '@styled-icons/fa-solid/Shield';
import { SignalWifiStatusbar4Bar as WifiOn } from '@styled-icons/material/SignalWifiStatusbar4Bar';
import { SignalWifiStatusbarConnectedNoInternet4 as WifiOff } from '@styled-icons/material/SignalWifiStatusbarConnectedNoInternet4';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip } from 'react-tooltip';
import { updateUI } from '../../store/ui-actions';
import useComponentRect from '../../hooks/useComponentRect';
import useOnboardingTooltip from '../../hooks/useOnboardingTooltip';

const Wrapper = styled.div`
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
  top: 0.75rem;
`;

const Container = styled.div`
  display: flex;
  position: absolute;
  right: 1rem;
  justify-content: space-between;
  align-items: center;
`;

const RoomInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-right: 0.75rem;
`;

const ConnectedText = styled.div`
  color: var(--clr-sec);
  font-size: 0.65rem;
  line-height: 1;
  margin-bottom: 2px;
`;

const RoomNameDisplay = styled.div`
  color: var(--clr-sec);
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1;
`;

const ReplayButton = styled(UndoAlt)`
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 1.8rem;
  height: 2rem;
  transform-origin: center;
  color: var(--clr-sec);

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(-360deg);
    }
  }

  &:hover {
    transform: scale(1.1) translateY(-1px);
    color: var(--clr-prim);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  }

  &:active {
    transform: scale(0.95);
    transition: all 0.1s ease;
  }

  animation: ${(props) =>
    props.replaying ? 'spin 2s linear infinite' : 'none'};

  outline: none;
  margin-left: 0.5rem;
`;

const SettingsButton = styled(Cog)`
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 1.8rem;
  height: 2rem;
  transform-origin: center;
  color: var(--clr-sec);

  &:hover {
    transform: rotate(90deg) scale(1.05) translateY(-1px);
    color: #ffffff;
    filter: drop-shadow(0 4px 8px rgba(255, 255, 255, 0.3));
  }

  &:active {
    transform: rotate(90deg) scale(0.95);
    transition: all 0.1s ease;
  }

  margin-left: 0.5rem;
`;

const RoomSwitchButton = styled(DoorOpen)`
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 1.8rem;
  height: 2rem;
  transform-origin: center;
  color: var(--clr-sec);

  &:hover {
    transform: scale(1.1) translateY(-2px) rotateY(10deg);
    color: #ffffff;
    filter: drop-shadow(0 6px 12px rgba(255, 255, 255, 0.3));
  }

  &:active {
    transform: scale(0.95);
    transition: all 0.1s ease;
  }

  margin-left: 0.5rem;
`;

const ModDashboardButton = styled(Shield)`
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 1.8rem;
  height: 2rem;
  transform-origin: center;
  color: var(--clr-sec);

  &:hover {
    transform: scale(1.1) translateY(-1px);
    color: #ffd700;
    filter: drop-shadow(0 4px 12px rgba(255, 215, 0, 0.4));
  }

  &:active {
    transform: scale(0.95);
    transition: all 0.1s ease;
  }

  margin-left: 0.5rem;
`;


const OnlineIndicatorContainer = styled.div`
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 2rem;
  width: 2rem;
  transform-origin: center;
  cursor: ${(props) => props.connected < 1 ? 'pointer' : 'default'};
  color: ${(props) => {
    if (props.connected >= 1) {
      return 'var(--clr-green)';
    } else if (props.connected >= 0) {
      return 'orange';
    } else {
      return 'red';
    }
  }};

  &:hover {
    transform: scale(1.05) translateY(-1px);
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.15));
  }
`;

function ButtonContainer(props) {
  const dispatch = useDispatch();

  const containerRef = useRef();
  const settingsRef = useRef();
  const roomSwitchRef = useRef();
  const settingsTooltipDisabled = useOnboardingTooltip(settingsRef, 1);
  const roomSwitchTooltipDisabled = useOnboardingTooltip(roomSwitchRef, 2);
  const componentRect = useComponentRect(containerRef);

  useEffect(() => {
    dispatch(updateUI('buttonContainer', componentRect));
  }, [dispatch, componentRect]);

  return (
    <Wrapper>
      <Container ref={containerRef}>
        {props.currentRoom && (
          <RoomInfoContainer>
            <ConnectedText>Connected to:</ConnectedText>
            <RoomNameDisplay>{props.currentRoom.title}</RoomNameDisplay>
          </RoomInfoContainer>
        )}
        <OnlineIndicatorContainer
          data-tooltip-id="button-container-tooltip" data-tooltip-content={
            props.connected >= 1
              ? `You are connected to VentScape${
                  props.currentRoom ? ` - ${props.currentRoom.title}` : '!'
                }`
              : "Can't connect to VentScape - Click to reconnect"
          }
          data-tooltip-place='bottom'
          connected={props.connected}
          onClick={props.connected < 1 ? props.onConnectionClick : undefined}
        >
          {props.connected >= 1 ? <WifiOn /> : <WifiOff />}
        </OnlineIndicatorContainer>
        {!props.useChatLayout && (
          <ReplayButton
            data-tooltip-id="button-container-tooltip" data-tooltip-content={
              props.replaying === true
                ? 'VentScape is replaying the last 10 messages'
                : 'Click to replay the last 10 messages'
            }
            data-tooltip-place='bottom'
            replaying={props.replaying}
            onClick={props.onClickReplay}
          />
        )}
        <RoomSwitchButton
          ref={roomSwitchRef}
          data-tooltip-id="room-switch-onboarding-tooltip" 
          data-tooltip-content='Switch to different chat rooms here'
          data-tooltip-place='bottom'
          onClick={props.onSwitchRoom}
        />
        {props.isMod && (
          <ModDashboardButton
            data-tooltip-id="button-container-tooltip" data-tooltip-content='Moderator Dashboard'
            data-tooltip-place='bottom'
            onClick={props.onClickReports}
          />
        )}
        {/* Friends button removed - integrated into DM/Friends sidebar */}
        <SettingsButton
          ref={settingsRef}
          data-tooltip-id="settings-onboarding-tooltip" 
          data-tooltip-content="Change your nickname and other settings here"
          onClick={props.onClickSettings}
        />
        <Tooltip 
          id="settings-onboarding-tooltip" 
          isOpen={!settingsTooltipDisabled}
          place="bottom"
          offset={10}
          anchorSelect='[data-tooltip-id="settings-onboarding-tooltip"]'
        />
        <Tooltip 
          id="room-switch-onboarding-tooltip" 
          isOpen={!roomSwitchTooltipDisabled}
          place="bottom"
          offset={10}
          anchorSelect='[data-tooltip-id="room-switch-onboarding-tooltip"]'
        />
        <Tooltip id="button-container-tooltip" />
      </Container>
    </Wrapper>
  );
}

export default ButtonContainer;
